/**
 * Where a sign-in link lands: `/auth/verify#token=…`.
 *
 * The token is in the URL fragment, which never reaches a server or a Referer
 * header. It's exchanged for the session cookie once (StrictMode's double
 * effect must not burn it), removed from the address bar, and the learner goes
 * where they were headed.
 */
import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { apiFetch } from '../../api/client';
import { useAuth } from '../../auth/AuthProvider';
import { takeReturnTo } from '../../auth/returnTo';
import { ErrorBanner } from '../ui/ErrorBanner';
import { LoadingState } from '../ui/LoadingState';
import styles from './SignIn.module.css';

type Failure = 'invalid' | 'invite' | 'network';

const MESSAGES: Record<Failure, string> = {
  invalid: 'This sign-in link is invalid or has expired.',
  invite: 'This invite code is no longer valid.',
  network: "We couldn't reach the server. Please try the link again.",
};

function tokenFromHash(hash: string): string | null {
  return new window.URLSearchParams(hash.replace(/^#/, '')).get('token');
}

export const VerifyPage: React.FC = () => {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [failure, setFailure] = useState<Failure | null>(null);
  // The token is single-use: StrictMode re-runs the effect, so exchange it only once.
  const exchanged = useRef<string | null>(null);

  useEffect(() => {
    const token = tokenFromHash(window.location.hash);
    // Keep the token out of history and anything that copies the URL.
    window.history.replaceState(null, '', window.location.pathname);
    if (!token) {
      // Also StrictMode's second run: the first one already took the token from the URL.
      if (exchanged.current === null) setFailure('invalid');
      return;
    }
    exchanged.current = token;

    void (async (): Promise<void> => {
      try {
        const response = await apiFetch(
          '/auth/verify',
          { method: 'POST', body: JSON.stringify({ token }) },
          { notifyUnauthorized: false }
        );
        if (response.ok) {
          await refresh();
          navigate(takeReturnTo(), { replace: true });
        } else {
          setFailure(response.status === 403 ? 'invite' : 'invalid');
        }
      } catch {
        setFailure('network');
      }
    })();
  }, [navigate, refresh]);

  if (!failure) {
    return <LoadingState variant="full-page" message="Signing you in…" />;
  }

  return (
    <main className={styles.page}>
      <div className={styles.card}>
        <h1 className={styles.title}>Couldn&apos;t sign you in</h1>
        <ErrorBanner message={MESSAGES[failure]} className={styles.banner} />
        <Link to="/sign-in" className={styles.linkButton}>
          Request a new link
        </Link>
      </div>
    </main>
  );
};

export default VerifyPage;
